// Discover — public tag-based browse page at /discover.
// Top section: pill cloud of all tags (clickable). Bottom: silo grid filtered
// by the selected tag (via ?tag=...) or full popularity-sorted list when no
// tag is selected.
//
// Search input handles free-text. Pressing enter routes to /discover?q=...
// (q + tag can be combined).
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, MagnifyingGlass, ArrowRight, Hash, Sparkle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import SiloArt from "@/components/SiloArt";

const hasArt = (silo) => !!silo?.cube?.faces?.center?.image_id;

export default function Discover() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tag = searchParams.get("tag") || "";
  const q = searchParams.get("q") || "";
  const [silos, setSilos] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState(q);

  useEffect(() => { setSearchInput(q); }, [q]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const params = {};
        if (tag) params.tag = tag;
        if (q) params.q = q;
        const [siloR, tagR] = await Promise.all([
          api.get("/discover", { params }),
          api.get("/discover/tags", { params: { limit: 30 } }),
        ]);
        if (cancel) return;
        setSilos(siloR.data?.silos || []);
        setTags(tagR.data?.tags || []);
      } catch {
        if (!cancel) { setSilos([]); setTags([]); }
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [tag, q]);

  const submitSearch = (e) => {
    e.preventDefault();
    const next = {};
    if (tag) next.tag = tag;
    if (searchInput.trim()) next.q = searchInput.trim();
    setSearchParams(next, { replace: false });
  };

  const clearTag = () => {
    const next = {};
    if (q) next.q = q;
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#FDFCF8]" data-testid="discover-page">
      <div className="border-b border-[#E5E2DD] bg-white sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="text-sm text-[#6B6A68] hover:text-[#1C1B1A] flex items-center gap-1">
            <ArrowLeft size={14} weight="duotone" /> Back
          </Link>
          <Link to="/" className="text-sm text-[#1C1B1A] font-serif-display">My Silo</Link>
        </div>
      </div>

      <main className="siloart-copy max-w-6xl mx-auto px-4 py-10">
        <h1 className="font-serif-display text-4xl text-[#1C1B1A] mb-2">Discover silos</h1>
        <p className="text-sm text-[#6B6A68] mb-8 max-w-2xl">
          Browse closed-corpus AI assistants by topic. Each silo is curated by a real creator — no public-web noise, only the source they trust.
        </p>

        {/* Search bar */}
        <form onSubmit={submitSearch} className="mb-8 max-w-xl" data-testid="discover-search-form">
          <div className="flex items-center gap-2">
            <div className="flex-1 relative">
              <MagnifyingGlass size={16} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6A68]" />
              <input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by name or tagline…"
                data-testid="discover-search-input"
                className="w-full bg-white border hairline rounded-md pl-9 pr-4 py-2.5 focus:outline-none focus:ring-1 focus:ring-[#4A5D4E] text-sm"
              />
            </div>
            <button
              type="submit"
              data-testid="discover-search-submit"
              className="px-4 py-2.5 bg-[#1C1B1A] text-white text-sm rounded-md hover:bg-[#3A4A3E] transition"
            >
              Search
            </button>
          </div>
        </form>

        {/* Tag pill cloud */}
        {tags.length > 0 && (
          <section className="mb-10" data-testid="discover-tag-cloud">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#A89360] mb-3 flex items-center gap-1.5">
              <Hash size={12} weight="duotone" /> Browse by tag
            </div>
            <div className="flex flex-wrap gap-1.5">
              {tags.map(({ tag: t, count }) => {
                const active = tag === t;
                return (
                  <Link
                    key={t}
                    to={`/discover?tag=${encodeURIComponent(t)}${q ? `&q=${encodeURIComponent(q)}` : ""}`}
                    data-testid={`discover-tag-pill-${t}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition ${active ? "bg-[#1C1B1A] text-white" : "bg-white border border-[#E5E2DD] text-[#1C1B1A] hover:border-[#C16E5A]"}`}
                  >
                    {t}
                    <span className={active ? "text-white/60" : "text-[#A89360]"}>
                      {count}
                    </span>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* Active filter row */}
        {(tag || q) && (
          <div className="mb-6 flex items-center gap-2 flex-wrap text-sm text-[#6B6A68]" data-testid="discover-active-filters">
            <span>Filtering by:</span>
            {tag && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#1C1B1A] text-white text-xs">
                tag: {tag}
                <button onClick={clearTag} className="hover:text-[#C16E5A]" data-testid="discover-clear-tag">×</button>
              </span>
            )}
            {q && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#4A5D4E] text-white text-xs">
                "{q}"
                <button
                  onClick={() => setSearchParams(tag ? { tag } : {}, { replace: true })}
                  className="hover:text-[#C16E5A]"
                  data-testid="discover-clear-query"
                >
                  ×
                </button>
              </span>
            )}
          </div>
        )}

        {/* Silo grid */}
        {loading ? (
          <div className="text-sm text-[#6B6A68]">Loading…</div>
        ) : silos.length === 0 ? (
          <div className="text-center py-16 text-[#6B6A68]" data-testid="discover-empty-state">
            <p className="text-sm">No silos match those filters yet.</p>
            {(tag || q) && (
              <button
                onClick={() => setSearchParams({}, { replace: true })}
                className="text-sm text-[#C16E5A] hover:underline mt-2"
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="discover-silo-grid">
            {silos.map((s) => <SiloResultCard key={s.id} silo={s} />)}
          </div>
        )}
      </main>
    </div>
  );
}

function SiloResultCard({ silo }) {
  return (
    <Link
      to={`/s/${silo.slug}`}
      className="block bg-white border border-[#E5E2DD] hover:border-[#C16E5A] rounded-lg p-5 transition group"
      data-testid={`discover-silo-card-${silo.slug}`}
    >
      {/* Oct 2026 (Randy) — the discover grid wears the same card as the
          creator profile: the silo's own art beside its name. A silo with no
          cover keeps the lettered tile so the row still reads as a row. */}
      <div className="flex items-start gap-3 mb-2">
        {hasArt(silo) ? (
          <SiloArt cube={silo.cube} enabled className="w-12 h-12 shrink-0" data-testid={`discover-silo-art-${silo.slug}`} />
        ) : (
          <div
            className="w-12 h-12 shrink-0 rounded-xl bg-[#F0F1EE] text-[#4A5D4E] flex items-center justify-center font-serif-display text-lg"
            data-testid={`discover-silo-art-fallback-${silo.slug}`}
          >
            {(silo.name || "?").trim().charAt(0).toUpperCase()}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <h3 className="font-serif-display text-lg text-[#1C1B1A] group-hover:text-[#C16E5A] transition truncate">{silo.name}</h3>
          {silo.tagline && (
            <p className="text-xs text-[#6B6A68] italic line-clamp-2">{silo.tagline}</p>
          )}
        </div>
      </div>
      {silo.interest_matches > 0 && (
        <div data-testid={`discover-match-${silo.slug}`} className="mb-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] text-[#4A5D4E] bg-[#4A5D4E]/10 rounded-full px-2.5 py-0.5">
          <Sparkle size={11} weight="fill" /> matches {silo.interest_matches} of your interest{silo.interest_matches === 1 ? "" : "s"}
        </div>
      )}
      {silo.creator_name && silo.creator_referral_code && (
        <Link
          to={`/u/${silo.creator_referral_code}`}
          onClick={(e) => e.stopPropagation()}
          className="text-[11px] text-[#A89360] hover:text-[#C16E5A] transition mb-2 block"
        >
          by {silo.creator_name}
        </Link>
      )}
      {silo.tags && silo.tags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {silo.tags.slice(0, 4).map((t) => (
            <Link
              key={t}
              to={`/discover?tag=${encodeURIComponent(t)}`}
              onClick={(e) => e.stopPropagation()}
              className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#4A5D4E]/10 text-[#4A5D4E] hover:bg-[#4A5D4E]/20 transition"
            >
              {t}
            </Link>
          ))}
        </div>
      )}
      <div className="text-[11px] text-[#A89360] flex items-center gap-1 mt-3">
        Visit silo <ArrowRight size={11} />
      </div>
    </Link>
  );
}
